// The brush mask of the Releases pixelation overlay: where the effect shows on
// one banner. A mask is a start value plus brush strokes, saved in the scene
// per template (`pixelationMasks`). Coordinates are fractions of the banner
// size, so a mask keeps its place when the banner is drawn at another size.
//
// The mask value is 0–1: 1 shows the effect, 0 hides it. Dots scale their area
// by it and squares their alpha; a soft brush gives values between.

/** One brush stroke. Short keys keep saved scenes small. */
export type MaskStroke = {
  /** 1 paints the effect in, 0 erases it. */
  v: 0 | 1;
  /** Brush radius as a fraction of the banner width. */
  r: number;
  /** Softness 0–1: the part of the radius that fades out. */
  s: number;
  /** Points as flat [x0, y0, x1, y1, …], fractions of width and height. */
  p: number[];
};

export type PixelationMask = {
  /** The value before the strokes: 1 = effect everywhere, 0 = nowhere. */
  base: 0 | 1;
  strokes: MaskStroke[];
};

/** The mask of a banner that has none: the effect shows everywhere. */
export const FULL_MASK: Readonly<PixelationMask> = Object.freeze({base: 1, strokes: []});

/** True when the mask shows the effect everywhere, so sampling can be skipped. */
export function isFullMask(mask: Readonly<PixelationMask>): boolean {
  return mask.base === 1 && mask.strokes.length === 0;
}

const round4 = (n: number) => Math.round(n * 10000) / 10000;

/**
 * Append a point to a stroke when it is far enough from the last one (`minStep`,
 * in px). Coordinates are px of a (width × height) banner; the stroke keeps
 * fractions rounded to 4 decimals. Returns true when the point was added.
 */
export function addStrokePoint(
  stroke: MaskStroke,
  x: number,
  y: number,
  width: number,
  height: number,
  minStep: number
): boolean {
  const n = stroke.p.length;
  if (n >= 2) {
    const dx = x - stroke.p[n - 2] * width;
    const dy = y - stroke.p[n - 1] * height;
    if (dx * dx + dy * dy < minStep * minStep) return false;
  }
  stroke.p.push(round4(x / width), round4(y / height));
  return true;
}

/** Start a stroke at (x, y) px. `radius` is px of a banner `width` px wide. */
export function startStroke(
  value: 0 | 1,
  x: number,
  y: number,
  radius: number,
  softness: number,
  width: number,
  height: number
): MaskStroke {
  const stroke: MaskStroke = {
    v: value,
    r: round4(radius / width),
    s: Math.round(Math.min(1, Math.max(0, softness)) * 100) / 100,
    p: [],
  };
  addStrokePoint(stroke, x, y, width, height, 0);
  return stroke;
}

/** Throw when saved data is not a mask, so a bad scene fails loudly. */
export function assertMask(value: unknown, id: string): asserts value is PixelationMask {
  const m = value as PixelationMask;
  if (!m || (m.base !== 0 && m.base !== 1) || !Array.isArray(m.strokes)) {
    throw new Error(`pixelationMasks["${id}"] is not a mask: ${JSON.stringify(value)}`);
  }
}

/**
 * Draw one stroke into a mask raster that is (width × height) × `scale` px.
 * Painting adds white; erasing removes alpha.
 */
export function drawMaskStroke(
  ctx: CanvasRenderingContext2D,
  stroke: MaskStroke,
  width: number,
  height: number,
  scale: number
): void {
  const r = stroke.r * width * scale;
  if (!(r > 0) || stroke.p.length < 2) return;
  const blur = r * stroke.s * 0.5;
  const core = Math.max(0.5, r - blur);
  ctx.save();
  ctx.globalCompositeOperation = stroke.v ? "source-over" : "destination-out";
  // `filter` is absent in old Safari; there the brush is hard.
  if (blur > 0.5 && "filter" in ctx) ctx.filter = `blur(${blur.toFixed(2)}px)`;
  ctx.fillStyle = "#fff";
  ctx.strokeStyle = "#fff";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = core * 2;
  const px = (i: number) => stroke.p[i] * width * scale;
  const py = (i: number) => stroke.p[i + 1] * height * scale;
  if (stroke.p.length === 2) {
    ctx.beginPath();
    ctx.arc(px(0), py(0), core, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(px(0), py(0));
    for (let i = 2; i < stroke.p.length; i += 2) ctx.lineTo(px(i), py(i));
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Rasterize a mask into `ctx`, which is (width × height) × `scale` px: white
 * with alpha = mask value. `extra` is a stroke in progress, drawn last.
 */
export function paintMask(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  scale: number,
  mask: Readonly<PixelationMask>,
  extra: MaskStroke | null = null
): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = "source-over";
  ctx.clearRect(0, 0, w, h);
  if (mask.base) {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
  }
  ctx.restore();
  for (const stroke of mask.strokes) drawMaskStroke(ctx, stroke, width, height, scale);
  if (extra) drawMaskStroke(ctx, extra, width, height, scale);
}

/**
 * A point sampler over rasterized mask pixels (RGBA, `maskW` × `maskH`) for a
 * banner `width` px wide: (x, y) in banner px → 0–1.
 */
export function maskSampler(
  data: Uint8ClampedArray,
  maskW: number,
  maskH: number,
  width: number,
  height: number
): (x: number, y: number) => number {
  const sx = maskW / width;
  const sy = maskH / height;
  return (x, y) => {
    const mx = Math.min(maskW - 1, Math.max(0, Math.floor(x * sx)));
    const my = Math.min(maskH - 1, Math.max(0, Math.floor(y * sy)));
    return data[(my * maskW + mx) * 4 + 3] / 255;
  };
}
